import { useEffect, useMemo, useState } from "react";
import type { VisitRecord } from "../../types/domain";
import { formatDate, formatDateForFilter, formatTime } from "../../utils/dateTime";

type OpenVisitsPanelProps = {
  records: VisitRecord[];
  isRefreshing: boolean;
  onCloseVisits: (records: VisitRecord[]) => void;
  onPreviewSignature: (path: string, label: string) => void;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function daysSince(visitDate: string, today: string) {
  return Math.round((Date.parse(today) - Date.parse(visitDate)) / DAY_MS);
}

function describePresence(days: number) {
  if (days <= 0) {
    return "Oggi";
  }

  return days === 1 ? "Da ieri" : `Da ${days} giorni`;
}

export function OpenVisitsPanel({
  records,
  isRefreshing,
  onCloseVisits,
  onPreviewSignature,
}: OpenVisitsPanelProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const today = formatDateForFilter(new Date());
  const staleRecords = useMemo(
    () => records.filter((record) => record.visitDate < today),
    [records, today],
  );

  // Mantiene la selezione solo sulle visite ancora presenti nell'elenco.
  useEffect(() => {
    setSelectedIds((current) => {
      const visibleIds = new Set(records.map((record) => record.visitId));
      const next = new Set([...current].filter((visitId) => visibleIds.has(visitId)));
      return next.size === current.size ? current : next;
    });
  }, [records]);

  const selectedRecords = records.filter((record) => selectedIds.has(record.visitId));
  const allSelected = records.length > 0 && selectedRecords.length === records.length;

  function toggleRecord(visitId: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(visitId)) {
        next.delete(visitId);
      } else {
        next.add(visitId);
      }
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds(
      allSelected ? new Set() : new Set(records.map((record) => record.visitId)),
    );
  }

  return (
    <section className="admin-panel">
      <div className="admin-panel__header">
        <div>
          <h2>Visite aperte</h2>
          <p>
            Presenze attualmente in sede. Registra l'uscita per chi è andato via senza
            firmare: finché la visita resta aperta il visitatore non può registrare un
            nuovo ingresso.
          </p>
        </div>
        <span className="summary-chip">{records.length} aperte</span>
      </div>

      {records.length === 0 ? (
        <div className="empty-state">
          Non risultano visite aperte con i filtri correnti.
        </div>
      ) : (
        <>
          <div className="bulk-bar">
            <label className="bulk-bar__check">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span>Seleziona tutte</span>
            </label>
            {staleRecords.length > 0 ? (
              <button
                className="ghost-button ghost-button--sm"
                type="button"
                onClick={() =>
                  setSelectedIds(new Set(staleRecords.map((record) => record.visitId)))
                }
              >
                Seleziona giorni precedenti ({staleRecords.length})
              </button>
            ) : null}
            <button
              className="primary-button primary-button--sm bulk-bar__submit"
              type="button"
              disabled={selectedRecords.length === 0}
              onClick={() => onCloseVisits(selectedRecords)}
            >
              Registra uscita
              {selectedRecords.length > 0 ? ` (${selectedRecords.length})` : ""}
            </button>
          </div>

          <div className={`data-table-wrap ${isRefreshing ? "is-refreshing" : ""}`}>
            <table className="data-table data-table--selectable">
              <thead>
                <tr>
                  <th scope="col" className="data-table__select">
                    <span className="visually-hidden">Selezione</span>
                  </th>
                  <th scope="col">Visitatore</th>
                  <th scope="col">Ingresso</th>
                  <th scope="col">In sede</th>
                  <th scope="col" className="data-table__actions-head">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => {
                  const days = daysSince(record.visitDate, today);
                  const isSelected = selectedIds.has(record.visitId);

                  return (
                    <tr
                      key={record.visitId}
                      className={`${days > 0 ? "is-stale" : ""} ${isSelected ? "is-selected" : ""}`}
                    >
                      <td className="data-table__select">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRecord(record.visitId)}
                          aria-label={`Seleziona ${record.firstName} ${record.lastName}`}
                        />
                      </td>
                      <td className="data-table__primary">
                        <strong>
                          {record.firstName} {record.lastName}
                        </strong>
                        <span>{record.company}</span>
                      </td>
                      <td data-label="Ingresso">
                        {formatDate(record.checkinAt)} · {formatTime(record.checkinAt)}
                      </td>
                      <td data-label="In sede">
                        <span
                          className={`status-pill ${
                            days > 0 ? "status-pill--warning" : "status-pill--open"
                          }`}
                        >
                          {describePresence(days)}
                        </span>
                      </td>
                      <td className="data-table__actions">
                        <button
                          className="ghost-button ghost-button--sm"
                          type="button"
                          onClick={() =>
                            onPreviewSignature(record.checkinSignaturePath, "Firma arrivo")
                          }
                        >
                          Firma arrivo
                        </button>
                        <button
                          className="ghost-button ghost-button--sm ghost-button--brand"
                          type="button"
                          onClick={() => onCloseVisits([record])}
                        >
                          Registra uscita
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
